import React, { useDeferredValue, useMemo } from "react";

import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";

import { DisplayAlert } from "@/components/alerts/add/DisplayAlert";
import { useBulletinTypeOptions } from "@/components/alerts/add-v2/hooks/useOptionLists";
import { useBulletinValues } from "@/components/alerts/add-v2/state/bulletinFormContext";
import BulletinCard from "@/components/alerts/BulletinCard";
import { defaultErrors } from "@/components/alerts/constants";
import { SingleAlertContext } from "@/components/alerts/context";

import { toAlertProperties } from "./toAlertProperties";
import { toBulletinCardResource } from "./toBulletinCardResource";

export type PreviewView = "full" | "card";

interface BulletinPreviewProps {
  view: PreviewView;
}

const noop = () => undefined;

/**
 * Full view / Card view.
 *
 * It lives apart from the preview it switches because it belongs in the preview's header, which is
 * the shell's — pinned above the preview on lg, and in the sheet's top bar below it. Scrolling the
 * preview away from its own view switch was never the intent.
 */
export const PreviewViewTabs: React.FC<{
  view: PreviewView;
  onViewChange: (view: PreviewView) => void;
}> = ({ view, onViewChange }) => {
  const { t } = useTranslation("bulletin/create-v2");

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={view}
      onChange={(_event, next: PreviewView | null) =>
        next && onViewChange(next)
      }
    >
      <ToggleButton value="full">{t("preview.full")}</ToggleButton>
      <ToggleButton value="card">{t("preview.card")}</ToggleButton>
    </ToggleButtonGroup>
  );
};

/**
 * The live preview.
 *
 * Full view reuses the production `DisplayAlert` unmodified by adapting the form values back into
 * the shape its context expects — the alternative was maintaining a second copy of ~800 lines of
 * bulletin rendering that would drift from the real one.
 *
 * Card view reuses the production `BulletinCard` on the same terms: it is what the officer is
 * writing *for*, so a second implementation of it would be a second thing to keep true.
 *
 * Both are **non-interactive** — `pointerEvents: none` over the card. It is a picture of a board
 * card, not a card: its share button navigates to `?bulletinId=…&access=true`, which from a form
 * that has not been saved would take the officer off the page and out of a bulletin with no id.
 */
export const BulletinPreview: React.FC<BulletinPreviewProps> = ({ view }) => {
  const values = useBulletinValues();
  // The list that turns `auto_theft` into "Auto Theft" — the card's own title (decision 5).
  const { getOptionLabel: bulletinTypeLabel } = useBulletinTypeOptions();

  // Keeps typing responsive: the preview tree re-renders at React's convenience rather than on
  // every keystroke. Phase 8 revisits this if the profiler says it is not enough.
  const deferredValues = useDeferredValue(values);
  const alertProperties = useMemo(
    () => toAlertProperties(deferredValues),
    [deferredValues],
  );

  const cardResource = useMemo(
    () =>
      toBulletinCardResource(
        deferredValues,
        bulletinTypeLabel(deferredValues.bulletinType ?? ""),
      ),
    [deferredValues, bulletinTypeLabel],
  );

  const contextValue = useMemo(
    () => ({
      alertProperties,
      setAlertProperties: noop,
      setAlertProperty: noop,
      errors: defaultErrors,
      setErrors: noop,
      refreshAlertData: noop,
    }),
    [alertProperties],
  );

  return (
    <>
      {view === "full" ? (
        <SingleAlertContext.Provider value={contextValue}>
          <DisplayAlert
            mode="add"
            issuing_police_service={alertProperties.policeService?.name ?? ""}
            associatedCrimes={alertProperties.associatedCrimes.map(
              (crime) => crime.crime,
            )}
            occurrenceNumbers={alertProperties.occurrenceNumbers}
            issuedTime={alertProperties.issuedTime?.toISOString() ?? null}
            description={alertProperties.description}
            pdf={alertProperties.pdf}
            evidences={alertProperties.evidence_files}
          />
        </SingleAlertContext.Provider>
      ) : (
        /*
          `inert`, not just `pointerEvents: "none"`. The card renders a share `IconButton` whose
          handler navigates, and pointer-events only stops the mouse — a keyboard could still tab
          into the preview and leave the form. `inert` takes the whole subtree out of the tab
          order and out of the hit-testing, and `pointerEvents` stays as the mouse fallback for a
          browser that does not support it.

          Spread with a cast because React 18 has no `inert` prop in its JSX types; the attribute
          is lowercase, so React passes it through to the DOM unchanged.
        */
        <Box
          {...({ inert: "" } as { inert?: string })}
          sx={{ pointerEvents: "none" }}
        >
          <BulletinCard
            index={0}
            alertItem={cardResource}
            alertClickHandler={noop}
            showFlag={false}
          />
        </Box>
      )}
    </>
  );
};
